import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { irParaAba, useAbaAtiva } from '@shared/hooks/useAbaAtiva';

export interface Aba {
  id: string;
  rotulo: string;
  conteudo: ReactNode;
}

/**
 * Diz aos blocos que eles estão dentro de uma aba.
 *
 * Bloco solto na página carrega a própria largura e o próprio respiro. Dentro
 * da moldura, quem manda na largura é a moldura, senão a página ganha duas
 * margens, uma dentro da outra.
 */
const DentroDaAba = createContext(false);
export const useDentroDaAba = () => useContext(DentroDaAba);

/**
 * As abas da página, desenhadas como abas de planilha.
 *
 * Dois cuidados que fazem a diferença entre abas e um acordeão disfarçado:
 *
 * O painel fechado continua no HTML, com o atributo `hidden`. Some da tela e
 * do leitor de tela, mas o buscador continua lendo. Se fosse desmontado, a
 * maior parte do conteúdo do site sumiria do Google.
 *
 * Cada aba tem endereço. Abrir `#quem-facilita` cai direto na aba certa, e o
 * botão voltar do navegador anda entre elas.
 *
 * NO CELULAR A TIRA NÃO CABE. São dez abas, 929 pixels de conteúdo numa
 * janela de 343. A tira mostrava três e meia, cortava a quarta no meio da
 * palavra, e nada dizia que existiam mais seis. A navegação inteira do site
 * ficava escondida atrás de uma rolagem lateral que ninguém adivinha.
 *
 * Abaixo de 640 pixels a tira sai e entram dois controles: uma lista suspensa
 * no topo da moldura, e um botão flutuante que aparece depois que a lista
 * suspensa sobe para fora da tela. Os dois mexem no mesmo estado da tira, que
 * continua sendo a fonte da verdade. Acima de 640 nada muda.
 */
export function Abas({ abas, padrao }: { abas: Aba[]; padrao: string }) {
  const ids = abas.map((aba) => aba.id);
  const atual = useAbaAtiva(ids, padrao);
  const moldura = useRef<HTMLDivElement>(null);
  const tira = useRef<HTMLDivElement>(null);
  const idSelecao = useId();

  /*
    A TIRA DECIDE SOZINHA SE CABE.

    Antes isso era um ponto de corte fixo em 640 pixels, e quebrou duas vezes
    pelo mesmo motivo: a medida das dez abas nao e constante. Ela mudou quando
    a pele trocou a fonte de texto para Inter, que e mais larga, e as mesmas
    palavras passaram de 929 para 967 pixels. Qualquer numero cravado aqui
    vira mentira no dia em que alguem reescrever um rotulo.

    Entao a tira mede a si mesma: se o conteudo nao cabe na largura visivel,
    ela some e a lista suspensa assume. Funciona para mudanca de fonte, de
    texto, de zoom do navegador e de tamanho de janela.
  */
  const [cabe, setCabe] = useState(true);

  useEffect(() => {
    const elemento = tira.current;
    const container = moldura.current;
    if (!elemento || !container) return;

    function medir() {
      if (!elemento) return;
      /* Tolerancia de 1px: arredondamento de subpixel nao conta como estouro. */
      setCabe(elemento.scrollWidth <= elemento.clientWidth + 1);
    }

    medir();

    /*
      Observa o CONTAINER, nao a tira.

      Observar a propria tira trava: quando ela nao cabe, encolhe para altura
      zero, para de mudar de tamanho, o observador nunca mais dispara e a
      medida fica presa em "nao cabe" para sempre. O container continua no
      fluxo e acompanha a janela, entao e ele que avisa.
    */
    const observador = new ResizeObserver(medir);
    observador.observe(container);

    /* As fontes chegam depois da primeira pintura e mudam a medida. */
    document.fonts?.ready.then(medir).catch(() => {});

    return () => observador.disconnect();
  }, [abas.length]);

  function abrir(id: string, rolar: boolean) {
    irParaAba(id);
    if (rolar) moldura.current?.scrollIntoView({ block: 'start' });
  }

  /* Setas andam entre as abas, como manda o padrão de tablist. */
  function aoTeclar(evento: React.KeyboardEvent, indice: number) {
    const passo = evento.key === 'ArrowRight' ? 1 : evento.key === 'ArrowLeft' ? -1 : 0;
    if (passo === 0) return;
    evento.preventDefault();
    const proxima = abas[(indice + passo + abas.length) % abas.length];
    if (!proxima) return;
    abrir(proxima.id, false);
    document.getElementById(`aba-${proxima.id}`)?.focus();
  }

  return (
    <div
      ref={moldura}
      className="mx-auto w-full max-w-5xl scroll-mt-20 px-4 pt-6 pb-16 sm:px-8 sm:pt-8"
    >
      {/*
        O controle do celular. Select nativo de propósito: abre o seletor do
        próprio sistema, já sabe teclado e leitor de tela, e não depende de
        nenhum código nosso para funcionar.
      */}
      <div className={`mb-3 ${cabe ? 'sm:hidden' : ''}`}>
        <label htmlFor={idSelecao} className="rotulo">
          Parte do curso
        </label>
        <div className="relative mt-1.5">
          <select
            id={idSelecao}
            value={atual}
            onChange={(evento) => abrir(evento.target.value, false)}
            className="w-full appearance-none border border-linha-forte bg-papel py-3 pr-10 pl-3.5 text-apoio font-medium text-tinta"
          >
            {abas.map((aba, indice) => (
              <option key={aba.id} value={aba.id}>
                {String(indice + 1).padStart(2, '0')}. {aba.rotulo}
              </option>
            ))}
          </select>
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-3.5 flex items-center text-tinta-fraca"
          >
            ▾
          </span>
        </div>
      </div>

      <div
        ref={tira}
        role="tablist"
        aria-label="Partes do curso"
        aria-hidden={!cabe}
        /*
          Quando nao cabe, ela some pela ALTURA e continua no fluxo, ocupando a
          largura do container.

          A primeira tentativa escondia com posicao absoluta, e isso criou um
          travamento: fora do fluxo o elemento se ajusta ao proprio conteudo,
          entao `scrollWidth` passava a ser igual a `clientWidth` e a medida
          dizia "cabe" para sempre. A medicao mudava a coisa medida.

          Com altura zero a largura continua sendo a do container, a conta
          `scrollWidth > clientWidth` continua valendo, e o ResizeObserver
          continua recebendo as mudancas de largura da janela.
        */
        className={`flex overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
          cabe ? '' : 'pointer-events-none h-0 opacity-0'
        }`}
      >
        {abas.map((aba, indice) => {
          const aberta = aba.id === atual;
          return (
            <button
              key={aba.id}
              id={`aba-${aba.id}`}
              role="tab"
              type="button"
              aria-selected={aberta}
              aria-controls={`painel-${aba.id}`}
              tabIndex={cabe && aberta ? 0 : -1}
              onClick={() => abrir(aba.id, false)}
              onKeyDown={(evento) => aoTeclar(evento, indice)}
              className={`relative -ml-px shrink-0 grow border border-linha-forte px-2 py-[0.6875rem] text-miudo whitespace-nowrap transition-colors first:ml-0 ${
                aberta
                  ? 'z-10 -mb-px border-b-papel bg-papel font-medium text-tinta'
                  : 'bg-papel-fundo text-tinta-suave hover:bg-papel/60 hover:text-tinta'
              }`}
            >
              {aba.rotulo}
            </button>
          );
        })}
      </div>

      <DentroDaAba.Provider value>
        {abas.map((aba) => (
          <div
            key={aba.id}
            id={`painel-${aba.id}`}
            role="tabpanel"
            aria-labelledby={`aba-${aba.id}`}
            hidden={aba.id !== atual}
            tabIndex={0}
            className={`min-h-[26rem] border border-linha-forte bg-papel px-5 py-2 sm:px-10 ${cabe ? 'sm:border-t-0' : ''}`}
          >
            {aba.conteudo}
          </div>
        ))}
      </DentroDaAba.Provider>

      <AtalhoDeSecoes abas={abas} atual={atual} aoEscolher={(id) => abrir(id, true)} />
    </div>
  );
}

/**
 * O botão flutuante do celular.
 *
 * Existe porque a lista suspensa fica no topo da moldura e sai da tela assim
 * que a pessoa desce dentro de uma seção. Sem ele, trocar de seção exige subir
 * a página inteira de volta.
 *
 * Só aparece depois que a lista suspensa passa. Antes disso seriam dois
 * controles idênticos na mesma tela, o que confunde em vez de ajudar.
 */
function AtalhoDeSecoes({
  abas,
  atual,
  aoEscolher,
}: {
  abas: Aba[];
  atual: string;
  aoEscolher: (id: string) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [visivel, setVisivel] = useState(false);
  const idMenu = useId();
  const caixa = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function aoRolar() {
      setVisivel(window.scrollY > 400);
    }
    aoRolar();
    window.addEventListener('scroll', aoRolar, { passive: true });
    return () => window.removeEventListener('scroll', aoRolar);
  }, []);

  /* Fecha sozinho quando some da tela, senão reaparece aberto ao rolar. */
  useEffect(() => {
    if (!visivel) setAberto(false);
  }, [visivel]);

  useEffect(() => {
    if (!aberto) return;

    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key === 'Escape') setAberto(false);
    }
    function aoTocarFora(evento: MouseEvent) {
      if (!caixa.current?.contains(evento.target as Node)) setAberto(false);
    }

    document.addEventListener('keydown', aoTeclar);
    document.addEventListener('mousedown', aoTocarFora);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.removeEventListener('mousedown', aoTocarFora);
    };
  }, [aberto]);

  if (!visivel) return null;

  return (
    <div
      ref={caixa}
      className="fixed right-4 bottom-5 z-40 sm:hidden"
    >
      {aberto ? (
        <div
          id={idMenu}
          className="absolute right-0 bottom-14 max-h-[60vh] w-60 overflow-y-auto border border-linha-forte bg-papel shadow-lg"
        >
          <ul>
            {abas.map((aba, indice) => {
              const ativa = aba.id === atual;
              return (
                <li key={aba.id} className="border-b border-linha last:border-b-0">
                  <button
                    type="button"
                    onClick={() => {
                      aoEscolher(aba.id);
                      setAberto(false);
                    }}
                    aria-current={ativa ? 'true' : undefined}
                    className={`flex min-h-11 w-full items-center gap-2.5 px-3.5 text-left text-apoio ${
                      ativa ? 'bg-papel-fundo font-medium text-tinta' : 'text-tinta-suave'
                    }`}
                  >
                    <span className="rotulo shrink-0 tabular-nums">
                      {String(indice + 1).padStart(2, '0')}
                    </span>
                    {aba.rotulo}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => setAberto((antes) => !antes)}
        aria-expanded={aberto}
        aria-haspopup="menu"
        aria-controls={aberto ? idMenu : undefined}
        aria-label={aberto ? 'Fechar as partes do curso' : 'Ver as partes do curso'}
        className="flex h-12 w-12 items-center justify-center border border-tinta bg-tinta text-papel shadow-lg transition-colors"
      >
        <span aria-hidden className="relative block h-3.5 w-5">
          {/*
            O hambúrguer desenhado com três fios, e virando X quando aberto.
            Sem biblioteca de ícone: são três retângulos e duas rotações.
          */}
          <span
            className={`absolute inset-x-0 top-0 h-px bg-papel transition-transform ${
              aberto ? 'translate-y-[6px] rotate-45' : ''
            }`}
          />
          <span
            className={`absolute inset-x-0 top-[6px] h-px bg-papel transition-opacity ${
              aberto ? 'opacity-0' : ''
            }`}
          />
          <span
            className={`absolute inset-x-0 top-[12px] h-px bg-papel transition-transform ${
              aberto ? '-translate-y-[6px] -rotate-45' : ''
            }`}
          />
        </span>
      </button>
    </div>
  );
}
